<script setup lang="ts">
import { ref, watch } from 'vue';
import api from '@/api';
import { MAX_TAG_NAME_LENGTH } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import ArticleTagsInput from '@/components/article/article-tags-input.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { GroupTag } from '@/types';

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });

const props = defineProps({
  groupId: { type: Number, required: true },
});

const emit = defineEmits<{
  close: [];
  submit: [
    {
      groupTag: GroupTag;
    },
  ];
}>();

watch(
  () => open.value,
  () => {
    if (!open.value) {
      inputValue.value = '';
      clearErrorMessage();
      emit('close');
    }
  },
);

const { createSnackbar } = useSnackbar();
const errorMessage = ref<string | undefined>(undefined);
const inputValue = ref('');
const isLoading = ref(false);
const DUPLICATE_ERROR_MESSAGE = '同じ名称のグループタグがあります。';

const clearErrorMessage = () => {
  errorMessage.value = undefined;
};

watch(() => inputValue.value, clearErrorMessage);

const submit = async () => {
  isLoading.value = true;
  try {
    if (inputValue.value === '') {
      errorMessage.value = 'グループタグを入力してください';
      return;
    }
    const groupTag = await api.createGroupTag(props.groupId, inputValue.value);
    emit('submit', { groupTag: groupTag });
    createSnackbar({
      message: 'グループタグを作成しました',
      type: 'success',
    });
    inputValue.value = '';
  } catch (e) {
    if (isAxiosError(e) && e.response?.data['error_type'] === 'duplicate') {
      errorMessage.value = DUPLICATE_ERROR_MESSAGE;
    } else {
      createSnackbar({
        message: 'グループタグの作成に失敗しました',
        type: 'error',
      });
      throw e;
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <DgrDialog v-model:open="open" title="グループタグを作成">
    <template #description>
      <div class="modal-content">
        <ArticleTagsInput
          v-model="inputValue"
          :error-message="errorMessage"
          :max-length-error-message="`${MAX_TAG_NAME_LENGTH}文字以内のグループタグが作成できます`"
        />
      </div>
    </template>

    <template #submit>
      <button
        type="button"
        class="c-btn c-btn--auto c-btn--AnewsPrimary"
        @click="submit"
        :disabled="isLoading"
      >
        保存
      </button>
    </template>
  </DgrDialog>
</template>
